import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useAppDispatch, useSelector } from "../../../../redux-store";
import {
  selectCargoDeletedByDispatcher,
  selectCargoDeletedByDriver,
} from "../../../../ducks/app/cargo-dialog/selectors";
import { selectDispatcher } from "../../../../ducks/data/dispatchers/selectors";
import { loadUsers } from "../../../../ducks/data/dispatchers";
import { selectDriver } from "../../../../ducks/data/drivers/selectors";

const fullName = (person: { firstName: string; lastName: string }) =>
  `${person.firstName} ${person.lastName}`.trim();

// Shown when someone else deleted the cargo open in the dialog (DORA-458).
// Names who did it when the dispatcher or driver can be found, otherwise says
// "someone else".
const DeletedCargoBanner = ({ cargoId }: { cargoId: string | null }) => {
  const { t } = useTranslation(["shipmentModal"]);
  const dispatch = useAppDispatch();
  const deletedBy = useSelector((state) =>
    cargoId ? selectCargoDeletedByDispatcher(cargoId)(state) : null
  );
  const deletedByDriver = useSelector((state) =>
    cargoId ? selectCargoDeletedByDriver(cargoId)(state) : null
  );
  const dispatcher = useSelector(selectDispatcher(deletedBy));
  // Drivers are loaded when the app starts (LoadStandardData).
  const driver = useSelector(selectDriver(deletedByDriver ?? undefined));

  // Dispatchers are only loaded on demand (e.g. the Log tab).
  useEffect(() => {
    if (deletedBy && !dispatcher) {
      dispatch(loadUsers());
    }
  }, [dispatch, deletedBy, dispatcher]);

  const text = dispatcher
    ? t("shipmentModal:deletedElsewhere.textByDispatcher", {
        name: fullName(dispatcher),
      })
    : driver
      ? t("shipmentModal:deletedElsewhere.textByDriver", {
          name: fullName(driver),
        })
      : t("shipmentModal:deletedElsewhere.text");

  return (
    <div
      className="shipment-dialog__errors-section"
      data-testid="cargo-deleted-banner"
    >
      <FontAwesomeIcon
        className="shipment-dialog-errors-section__icon"
        icon={faExclamationTriangle}
      />
      <div>
        <div className="shipment-dialog-errors-section__title">
          {t("shipmentModal:deletedElsewhere.title")}
        </div>
        <div>{text}</div>
      </div>
    </div>
  );
};

export default DeletedCargoBanner;
